import { CreatePinboardMessageRequest, TargetAssociation } from './CreatePinboardMessageRequest';
import { GetPinboardMessageResponse } from './GetPinboardMessageResponse';
import { UpdatePinboardMessageRequest } from './UpdatePinboardMessageRequest';

const formatTimeToHHMM = (date: Date): string => {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
};

const combineDateAndTime = (date: Date | undefined, time: string | undefined): Date | undefined => {
    if (!date) {
        return undefined;
    }
    const combined = new Date(date);
    if (time) {
        const [hours, minutes] = time.split(':').map(Number);
        combined.setHours(hours, minutes, 0, 0);
    }
    return combined;
};

export type PinboardMessageFormModel = {
    id: string;
    title: string;
    text: string;
    filePaths: string[];
    files?: File[] | undefined;
    canUserAnswer: string;
    isPinned: boolean;
    target: string;
    targetAssociations?: string[];
    date?: Date;
    time?: string;
    endDate?: Date;
    endTime?: string;
};

export const mapResponseToForm = (pinboardMessage: GetPinboardMessageResponse): PinboardMessageFormModel => {
    const publishDate = pinboardMessage.publishDate ? new Date(pinboardMessage.publishDate) : new Date();
    const unpublishDate = pinboardMessage.unpublishDate ? new Date(pinboardMessage.unpublishDate) : undefined;

    return {
        id: pinboardMessage.pinboardMessageId,
        title: pinboardMessage.title,
        text:
            pinboardMessage.text
                ?.replace(/&gt;/g, '>')
                .replace(/&lt;/g, '<')
                .replace(/<br\s*\/?>/gm, '\n') || '',
        files: [],
        filePaths: pinboardMessage.filePaths ?? [],
        canUserAnswer: pinboardMessage.usersCanRespond ? 'ja' : 'nej',
        isPinned: pinboardMessage.isPinned,
        target: pinboardMessage.propertyAssociationArea?.propertyAssociationAreaId || 'all',
        targetAssociations: [],
        date: publishDate,
        time: formatTimeToHHMM(publishDate),
        endDate: unpublishDate,
        endTime: unpublishDate ? formatTimeToHHMM(unpublishDate) : undefined,
    };
};

export const mapFormToCreateRequest = (form: PinboardMessageFormModel, targetAssociations: TargetAssociation[]): CreatePinboardMessageRequest => {
    const publishDate = combineDateAndTime(form.date, form.time);
    const unpublishDate = combineDateAndTime(form.endDate, form.endTime);

    return {
        files: form.files,
        isPinned: form.isPinned,
        targetAssociations,
        text: form.text?.replace(/>/g, '&gt;').replace(/</g, '&lt;').replace(/\n/g, '<br>') || '',
        title: form.title,
        usersCanRespond: form.canUserAnswer === 'ja',
        // HACK: The API doesn't support ISO format, have to send it with swedish locale in a combined format.
        publishDate: publishDate?.toLocaleString('sv-SE').replace(' ', 'T'),
        unpublishDate: unpublishDate?.toLocaleString('sv-SE').replace(' ', 'T'),
    };
};

export const mapFormToUpdateRequest = (form: PinboardMessageFormModel): UpdatePinboardMessageRequest => {
    const publishDate = combineDateAndTime(form.date, form.time);
    const unpublishDate = combineDateAndTime(form.endDate, form.endTime);

    return {
        files: form.files,
        filePaths: form.filePaths,
        isPinned: form.isPinned,
        text: form.text?.replace(/>/g, '&gt;').replace(/</g, '&lt;').replace(/\n/g, '<br>') || '',
        title: form.title,
        usersCanRespond: form.canUserAnswer === 'ja',
        // HACK: The API doesn't support ISO format, have to send it with swedish locale in a combined format.
        publishDate: publishDate?.toLocaleString('sv-SE').replace(' ', 'T'),
        unpublishDate: unpublishDate?.toLocaleString('sv-SE').replace(' ', 'T'),
    };
};
